import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { useQuery } from "@apollo/client";
import styled from 'styled-components'

import Header from "../Header/Header";
import { Black, Shadow, WerfenColor } from '../../Constants/ColorChart'
import { Col, Container, Row } from 'react-bootstrap'
import { RESOURCE_QUERY } from './Queries/resourceQuery'
import { getDocumentsFromResource, getFields as getFieldsToShow, processAsResource } from '../../Utils/utils'
import { hideSearchBar, setTitleBar, showBackArrow } from "../../../actions/ui";
import { setDocument } from '../../../actions/documentViewer'
import { setFavouriteResource } from '../../../actions/resource'
import Loader from '../Loader/Loader'
import Dialog from "../Dialog/Dialog";
import isElectron from "is-electron";
import ResultTable from "../ResultTable/ResultTable";

let electron;

if ( isElectron() ) {
    electron = window.require( "electron" )
}
const ipcRenderer = electron && electron.ipcRenderer;

const TableWrapper = styled.div`
  margin-top: 1%;
  &.no-header{
    margin-top: 150px;
  }
`;

const StyledDiv = styled.div`
    padding-bottom: 150px;
`;

const Resource = ( props ) => {
    const id = useParams().id;
    let history = useHistory();
    let resource;
    const { session } = props;
    const [ resourceIsDownloaded, setResourceIsDownloaded ] = useState( false );
    const [ availableOfflineChecked, setAvailableOfflineChecked ] = useState( false );
    const [ storedWithPlaylist, setStoredWithPlaylist ] = useState( [] );

    useEffect( () => {
        props.hideSearchBar();
        props.setTitleBar( "Resource" );
        props.showBackArrow();

        if ( !id ) {
            history.push( "/" )
        }

        if ( isElectron() ) {
            ipcRenderer.on( "deletionFinished", getDownloadedIds );
            ipcRenderer.on( "indexationFinished", getDownloadedIds );
            ipcRenderer.on( "finishedGetDownloadedIds", parseDownloadedIds );
            ipcRenderer.on( "finishedCheckIfStoredWithPlaylist", setIfStoredWithPlaylist );

            if ( !availableOfflineChecked ) {
                ipcRenderer.send( "checkIfStoredWithPlaylist", id );
                ipcRenderer.send( "getDownloadedItems", "ids" );
                setAvailableOfflineChecked( true );
            }
        }
        return () => {
            if ( isElectron() ) {
                ipcRenderer.off( "deletionFinished", getDownloadedIds );
                ipcRenderer.off( "indexationFinished", getDownloadedIds );
                ipcRenderer.off( "finishedGetDownloadedIds", parseDownloadedIds );
                ipcRenderer.off( "finishedCheckIfStoredWithPlaylist", setIfStoredWithPlaylist );
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [] );

    const getDownloadedIds = () => {
        ipcRenderer.send( "getDownloadedItems", "ids" );
    }

    const parseDownloadedIds = ( event, ids ) => {
        setResourceIsDownloaded( ids.map(item=>item.id).includes( id ) );
    }

    const setIfStoredWithPlaylist = ( event, data ) => {
        if ( data.length > 0 ) {
            setStoredWithPlaylist( !!data );
        } else {
            setStoredWithPlaylist( false )
        }
    }

    const separateDefaultFile = ( files ) => {
        let dataForTable = [];
        let dataForHeader = [];

        files && files.forEach( ( data, index ) => {
            index === 0 ? dataForHeader.push( data ) : dataForTable.push( data );
        } )
        return { dataForTable, dataForHeader }
    }


    const classifyResult = ( resource ) => {
        let output = [];
        if ( resource && resource.fieldResourceFilesByFormat.length > 0 ) {
            const processedResource = processAsResource( resource );
            const docs = getDocumentsFromResource( resource );
            const separatedFiles = separateDefaultFile( docs );
            const numberOfElements = docs ? docs.length : 0;
            const isResourceDownloaded = isElectron() ? resourceIsDownloaded : false;
            const isInPlaylist = isElectron() ? storedWithPlaylist : false;

            output = {
                id: id,
                title: resource.entityLabel,
                description: resource.fieldResourceDescription,
                docs,
                separatedFiles,
                numberOfElements,
                availableOffline: isResourceDownloaded,
                isInPlaylist,
                isResourceSet: true,
                processedResource
            }
        }
        return output;
    }

    const setFavoriteResourceStatus = ( isFav ) => {
        props.setFavouriteResource( isFav );
    }

    const { loading, error, data } = useQuery( RESOURCE_QUERY,
        {
            variables: { "id": String( id ) },
            fetchPolicy: "no-cache"
        } );

    if ( loading ) {
        return (
            <Loader
                message="Loading"
                primaryColor={ WerfenColor }
                secondaryColor={ Shadow }
                fontColor={ Black }
                extraStyle="margin-top: 10%"
            />
        )
    }

    if ( error ) {
        return (
            <StyledDiv>
                <div>
                    <h1>No data recovered</h1>
                </div>
            </StyledDiv>
        )
    }

    if ( data && data.nodeById ) {
        resource = classifyResult( data.nodeById );
    } else {
        resource = {
            title: "Error",
            description: "The Resource you are trying to access seems to be out of your scope. Press the back arrow and try another one.",
            elementCounter: 0,
            isFav: null,
        }
    }

    const title = decodeURIComponent( resource.title );
    const description = resource.description ? decodeURIComponent( resource.description ) : "";
    const { isFav, numberOfElements, downloadCounter, separatedFiles } = resource;
    let fieldsExcludes = ["name", "filesize", "entityBundle", "entityChanged", "entityCreated", "fileIndex", "tags"];
    if (process.env.REACT_APP_SEE_VERSION !== "1") {
      fieldsExcludes.push("version");
    }

    if (process.env.REACT_APP_SEE_DATES !== "1") {
      fieldsExcludes.push("fromDate", "releaseDate");
    }

    let fieldsToShow = [];
    if (separatedFiles && separatedFiles.dataForHeader[0] && separatedFiles.dataForHeader[0].metadata) {
      fieldsToShow = getFieldsToShow(separatedFiles.dataForHeader[0].metadata, fieldsExcludes, false);
    }

    let fieldsToShowOther = [];
    if (separatedFiles && separatedFiles.dataForHeader[0] && separatedFiles.dataForTable[0] && separatedFiles.dataForHeader[0].metadata) {
      fieldsToShowOther = getFieldsToShow(separatedFiles.dataForTable[0].metadata, fieldsExcludes, false);
    }

    return (
        <StyledDiv>
            <Header
                upperMiniTitle={ "Resource" }
                title={ decodeURIComponent( title ) }
                description={ description }
                isFav={ isFav }
                actionToChangeFav={ setFavoriteResourceStatus }
                availableOffline={ resourceIsDownloaded }
                elementCounter={ numberOfElements }
                downloadCounter={ downloadCounter }
                session={ session }
                resource={ resource }
                itemId={ resource.id }
                goBack={ false }
            />
            <Container fluid>
                <Row>
                    <Col xs={ 12 }>
                        <TableWrapper className={ title ? "" : "no-header" }>
                            <ResultTable
                                centered
                                rowWidth={ 40 }
                                title={ (separatedFiles && separatedFiles.dataForTable.length > 0) ? "" : "" }
                                fieldsToShow={ fieldsToShow }
                                data={ separatedFiles && separatedFiles.dataForHeader }
                                type={ "resource_detail" }
                                tableUnified={false}
                            />
                        </TableWrapper>
                    </Col>
                </Row>
            </Container>
            { resource && separatedFiles && separatedFiles.dataForTable && separatedFiles.dataForTable.length > 0 &&
            <Container fluid>
                <Row>
                    <Col xs={ 12 }>
                        <TableWrapper>
                            <ResultTable
                                centered
                                rowWidth={ 40 }
                                title={ "Other components for this resource" }
                                fieldsToShow={ fieldsToShowOther }
                                data={ separatedFiles && separatedFiles.dataForTable && separatedFiles.dataForTable }
                                type={ "resource_detail" }
                                tableUnified={false}
                            />
                        </TableWrapper>
                    </Col>
                </Row>
            </Container>
            }
            { props.dialogVisible &&
            <Dialog/>
            }
        </StyledDiv>
    )
}

const mapStateToProps = ( state ) => ({
    downloadManager: {
        dialogVisible: state.ui.dialogVisible
    },
    resource: state.resource,
    session: state.session
});

export default connect( mapStateToProps, {
    setFavouriteResource,
    setDocument,
    showBackArrow,
    setTitleBar,
    hideSearchBar,
} )( Resource )
